import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheckCircle, FiRotateCcw, FiXCircle } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "received", label: "Received" },
  { value: "cancelled", label: "Cancelled" },
];

const PAYMENT_METHOD_LABELS = {
  cash: "Cash",
  credit_card: "Card",
  room_charge: "Room Charge",
};

function getBusinessUnitId(searchParams) {
  const user = readStoredUser();
  return searchParams.get("business_unit_id") || user?.active_business_unit_id || user?.business_unit_ids?.[0] || "1";
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatMoney(value) {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(value || 0));
}

function statusChipClass(status) {
  if (status === "received") return "rm-chip--ok";
  if (status === "cancelled") return "rm-chip--off";
  return "rm-chip--soft";
}

function orderItemsSummary(order) {
  const lines = Array.isArray(order?.lines) ? order.lines : [];
  if (!lines.length) return "-";
  return lines.map((line) => `${Number(line.quantity || 0)} x ${line.item_name}`).join(", ");
}

export default function Orders() {
  const [searchParams] = useSearchParams();
  const businessUnitId = getBusinessUnitId(searchParams);
  const [orders, setOrders] = useState([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("new");
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const limit = 50;

  const hasPrevious = offset > 0;
  const hasNext = offset + limit < count;
  const newOrdersCount = useMemo(() => orders.filter((order) => order.status === "new").length, [orders]);

  const loadOrders = useCallback(async (nextOffset = 0, options = {}) => {
    try {
      if (!options.silent) setLoading(true);
      if (!options.silent) setError("");
      const res = await axios.get("/api/pos/guest-orders", {
        params: {
          business_unit_id: businessUnitId,
          q: debouncedSearchQuery,
          status: statusFilter,
          limit,
          offset: nextOffset,
        },
      });
      setOrders(res.data?.rows || []);
      setCount(res.data?.count || 0);
      setOffset(nextOffset);
    } catch (err) {
      if (!options.silent) setError(err?.response?.data?.error || "Could not load POS orders.");
    } finally {
      if (!options.silent) setLoading(false);
    }
  }, [businessUnitId, debouncedSearchQuery, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    loadOrders(0);
  }, [loadOrders]);

  useEffect(() => {
    const timer = setInterval(() => {
      loadOrders(offset, { silent: true });
    }, 10000);
    return () => clearInterval(timer);
  }, [loadOrders, offset]);

  const clearFilters = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setStatusFilter("");
  };

  const updateOrderStatus = async (order, status) => {
    try {
      setActionId(order.id);
      setError("");
      setMessage("");
      const res = await axios.patch(`/api/pos/guest-orders/${order.id}`, {
        business_unit_id: businessUnitId,
        status,
      });
      setMessage(`${order.order_number} marked as ${res.data?.status_label || status}.`);
      await loadOrders(offset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not update POS order.");
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="reservations-container rm-page">
      <div className="rm-toolbarCard">
        <div className="rm-toolbarRow">
          <div>
            <h3 className="rm-rates-title">Orders</h3>
            <div className="rm-rates-subtitle">
              {loading ? "Loading orders..." : `${count} orders · ${newOrdersCount} new on this page`}
            </div>
          </div>
          <div className="rm-topActions">
            <input
              className="rm-searchInput"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search order, name, room, payment or outlet"
            />
            <select
              className="posSelect posDeviceStatusSelect"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button type="button" className="rm-topBtn rm-topBtn--violet" onClick={clearFilters}>
              Clear
            </button>
          </div>
        </div>
      </div>

      {error ? <div className="posStateBanner">{error}</div> : null}
      {message ? <div className="posInfoBanner">{message}</div> : null}

      <div className="rm-rates-card">
        <div className="rm-rates-header">
          <h3 className="rm-rates-title">Tablet order queue</h3>
          <div className="rm-rates-subtitle">Incoming orders are refreshed automatically every 10 seconds.</div>
        </div>

        <div className="res-listMetaRow rm-listMetaRow">
          <small className="res-listMetaText">
            {loading ? "Loading orders..." : `Showing ${count ? offset + 1 : 0}-${Math.min(count, offset + orders.length)} of ${count}`}
          </small>
          <div className="rm-topActions">
            <button type="button" className="rm-topBtn" disabled={!hasPrevious} onClick={() => loadOrders(Math.max(0, offset - limit))}>
              Previous
            </button>
            <button type="button" className="rm-topBtn" disabled={!hasNext} onClick={() => loadOrders(offset + limit)}>
              Next
            </button>
          </div>
        </div>

        <div className="rm-rates-table-wrap">
          <table className="rm-rates-table posOrdersTable" style={{ "--rm-col1-width": "160px", "--rm-table-min-width": "1320px" }}>
            <colgroup>
              <col style={{ width: "160px" }} />
              <col style={{ width: "160px" }} />
              <col style={{ width: "170px" }} />
              <col style={{ width: "170px" }} />
              <col style={{ width: "380px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "250px" }} />
            </colgroup>
            <thead>
              <tr>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Order</span><span className="res-thSub">Number</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Submitted</span><span className="res-thSub">Time</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Guest</span><span className="res-thSub">Name</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Payment</span><span className="res-thSub">Method / room</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Items</span><span className="res-thSub">Order detail</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Total</span><span className="res-thSub">Estimated</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Status</span><span className="res-thSub">Queue</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Actions</span><span className="res-thSub">Service</span></div></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td>
                    <strong className="posDeviceName">{order.order_number}</strong>
                    <small className="posDeviceMeta">{[order.outlet_name || order.device?.outlet_name, order.table_name].filter(Boolean).join(" · ") || "-"}</small>
                  </td>
                  <td>{formatDate(order.submitted_at)}</td>
                  <td>
                    <strong className="posOrderGuest">{order.customer_name || "-"}</strong>
                  </td>
                  <td>
                    <strong className="posOrderGuest">
                      {order.payment_method_label || PAYMENT_METHOD_LABELS[order.payment_method_category] || "Not specified"}
                    </strong>
                    {order.payment_method_category === "room_charge" && order.room_number ? (
                      <small className="posDeviceMeta">Room {order.room_number}</small>
                    ) : null}
                  </td>
                  <td>
                    <div className="posOrderItems">{orderItemsSummary(order)}</div>
                    {order.order_note ? <small className="posOrderNote">{order.order_note}</small> : null}
                  </td>
                  <td>{formatMoney(order.total_amount)}</td>
                  <td>
                    <span className={`rm-chip ${statusChipClass(order.status)}`}>
                      {order.status_label}
                    </span>
                  </td>
                  <td>
                    <div className="posDeviceActions">
                      {order.status !== "received" ? (
                        <button
                          type="button"
                          className="rm-topBtn rm-topBtn--green"
                          onClick={() => updateOrderStatus(order, "received")}
                          disabled={actionId === order.id}
                        >
                          <FiCheckCircle /> Received
                        </button>
                      ) : null}
                      {order.status !== "new" ? (
                        <button
                          type="button"
                          className="rm-topBtn"
                          onClick={() => updateOrderStatus(order, "new")}
                          disabled={actionId === order.id}
                        >
                          <FiRotateCcw /> Reopen
                        </button>
                      ) : null}
                      {order.status !== "cancelled" ? (
                        <button
                          type="button"
                          className="rm-topBtn rm-topBtn--amber"
                          onClick={() => updateOrderStatus(order, "cancelled")}
                          disabled={actionId === order.id}
                        >
                          <FiXCircle /> Cancel
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
              {!orders.length ? (
                <tr>
                  <td colSpan="8" className="posMuted">No tablet orders found.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
